// Fleece template adapter: keep the cart closed while its post-add offer is shown.
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.insuranceStorageKey = 'cart-insurance-opt-out-v1';
    this.cartMutationQueue = Promise.resolve();
    this.insuranceTimer = null;
    this.cartUpdateUnsubscriber = undefined;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('change', this.onDrawerChange.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'cart-insurance') return;
        this.scheduleInsuranceReconcile(250);
      });
    }
    this.syncInsuranceUI();
    this.scheduleInsuranceReconcile(350);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    if (this.insuranceTimer) clearTimeout(this.insuranceTimer);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    this.syncInsuranceUI();
    this.scheduleInsuranceReconcile(100);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    if (!parsedState || !parsedState.sections) return;
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const html = parsedState.sections[section.id];
      if (!sectionElement || !html) return;
      const content = this.getSectionInnerHTML(html, section.selector);
      if (content !== null) sectionElement.innerHTML = content;
    }));

    this.classList.toggle('is-empty', Boolean(this.querySelector('.drawer__inner-empty')));
    this.syncInsuranceUI();
    if (!options.skipInsuranceSchedule) this.scheduleInsuranceReconcile(250);

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      if (options.open !== false) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* ---------- Shipping insurance ---------- */

  onDrawerChange(event) {
    const toggle = event.target.closest('[data-cart-insurance-toggle]');
    if (!toggle || !this.contains(toggle)) return;
    event.stopPropagation();
    this.updateInsurancePreference(toggle);
  }

  enqueueCartMutation(task) {
    const run = this.cartMutationQueue.catch(() => undefined).then(task);
    this.cartMutationQueue = run.catch(() => undefined);
    return run;
  }

  async requestCartMutation(url, payload, source, options = {}) {
    const body = {
      ...payload,
      sections: this.getSectionsToRender().map((section) => section.id).join(','),
      sections_url: window.location.pathname
    };
    const response = await fetch(url, { ...fetchConfig(), body: JSON.stringify(body) });
    const data = await this.parseCartResponse(response);
    const statusCode = Number(data.status);
    const hasErrorStatus = Number.isFinite(statusCode) && statusCode >= 400;
    const hasErrors = Array.isArray(data.errors)
      ? data.errors.length > 0
      : Boolean(data.errors && (typeof data.errors !== 'object' || Object.keys(data.errors).length));
    if (!response.ok || hasErrorStatus || hasErrors) {
      throw new Error(this.getCartErrorMessage(data));
    }
    if (options.render !== false) {
      this.renderContents(data, { open: false, skipInsuranceSchedule: options.skipInsuranceSchedule === true });
    }
    this.publishCartUpdate(source);
    return data;
  }

  getCartApiUrl(action) {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (root) {
      const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
      return `${normalizedRoot}cart${action ? `/${action}` : ''}.js`;
    }
    return `/cart${action ? `/${action}` : ''}.js`;
  }

  async parseCartResponse(response) {
    const responseText = await response.text();
    if (!responseText) return {};

    try {
      return JSON.parse(responseText);
    } catch (error) {
      throw new Error(`${window.cartStrings.error} (${response.status})`);
    }
  }

  getCartErrorMessage(data) {
    if (typeof data.description === 'string' && data.description) return data.description;
    if (typeof data.message === 'string' && data.message) return data.message;
    if (typeof data.errors === 'string' && data.errors) return data.errors;
    if (Array.isArray(data.errors) && data.errors.length) return data.errors.join(' ');
    if (data.errors && typeof data.errors === 'object') {
      const messages = Object.values(data.errors).flat().filter(Boolean);
      if (messages.length) return messages.join(' ');
    }
    return window.cartStrings.error;
  }

  publishCartUpdate(source) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, { source });
  }

  scheduleInsuranceReconcile(delay = 0) {
    if (this.dataset.insuranceEnabled !== 'true' && this.dataset.insuranceInCart !== 'true') return;
    if (this.insuranceTimer) clearTimeout(this.insuranceTimer);
    this.insuranceTimer = setTimeout(() => {
      this.insuranceTimer = null;
      this.reconcileInsurance();
    }, delay);
  }

  reconcileInsurance() {
    this.clearInsuranceError();
    return this.enqueueCartMutation(async () => {
      const cart = await this.fetchCart();
      await this.reconcileInsuranceState(cart);
      this.syncInsuranceUI();
    }).catch((error) => {
      console.error('Shipping insurance update failed:', error);
      this.showInsuranceError(error);
    });
  }

  isInsuranceItem(item) {
    return Boolean(item && item.properties && item.properties.__addon === 'insurance');
  }

  async removeInsuranceItem(insuranceItem) {
    return this.requestCartMutation(this.getCartApiUrl('change'), {
      id: insuranceItem.key,
      quantity: 0
    }, 'cart-insurance', { skipInsuranceSchedule: true });
  }

  async reconcileInsuranceState(cart) {
    const enabled = this.dataset.insuranceEnabled === 'true';
    const variantId = Number(this.dataset.insuranceVariantId || 0);
    const visibleItems = cart.items.filter((item) => !this.isInsuranceItem(item));
    let insuranceItem = cart.items.find((item) => this.isInsuranceItem(item));

    // Disabled, misconfigured, or nothing else in the cart: insurance must not stay alone.
    if (!enabled || !variantId || visibleItems.length === 0) {
      if (insuranceItem) await this.removeInsuranceItem(insuranceItem);
      this.dataset.insuranceInCart = 'false';
      return;
    }

    if (this.getInsuranceOptOut(cart.token)) {
      if (insuranceItem) await this.removeInsuranceItem(insuranceItem);
      this.dataset.insuranceInCart = 'false';
      return;
    }

    // Insurance product changed in theme settings: swap the old line out.
    if (insuranceItem && Number(insuranceItem.variant_id) !== variantId) {
      const updatedCart = await this.removeInsuranceItem(insuranceItem);
      insuranceItem = updatedCart.items && updatedCart.items.find((item) => this.isInsuranceItem(item));
    }

    if (!insuranceItem) {
      await this.requestCartMutation(this.getCartApiUrl('add'), {
        items: [{
          id: variantId,
          quantity: 1,
          properties: { __addon: 'insurance' }
        }]
      }, 'cart-insurance', { skipInsuranceSchedule: true });
      this.dataset.insuranceInCart = 'true';
      return;
    }

    this.dataset.insuranceInCart = 'true';
    if (insuranceItem.quantity !== 1) {
      await this.requestCartMutation(this.getCartApiUrl('change'), {
        id: insuranceItem.key,
        quantity: 1
      }, 'cart-insurance', { skipInsuranceSchedule: true });
    }
  }

  updateInsurancePreference(toggle) {
    const optedOut = !toggle.checked;
    const previousOptOut = !optedOut;
    toggle.disabled = true;
    this.clearInsuranceError();
    this.setInsuranceOptOut(optedOut, this.currentCartToken || '');
    this.syncInsuranceUI();

    this.enqueueCartMutation(async () => {
      const cart = await this.fetchCart();
      this.setInsuranceOptOut(optedOut, cart.token || '');
      await this.reconcileInsuranceState(cart);
      this.syncInsuranceUI();
    }).catch((error) => {
      console.error('Shipping insurance preference failed:', error);
      this.setInsuranceOptOut(previousOptOut, this.currentCartToken || '');
      this.syncInsuranceUI();
      this.showInsuranceError(error);
    }).finally(() => {
      const currentToggle = this.querySelector('[data-cart-insurance-toggle]');
      if (currentToggle) currentToggle.disabled = false;
    });
  }

  async fetchCart() {
    const response = await fetch(this.getCartApiUrl(), {
      headers: { Accept: 'application/json' }
    });
    const cart = await this.parseCartResponse(response);
    if (!response.ok) throw new Error(this.getCartErrorMessage(cart));
    this.currentCartToken = cart.token || '';
    return cart;
  }

  getInsurancePreference() {
    try {
      return JSON.parse(localStorage.getItem(this.insuranceStorageKey) || '{}');
    } catch (error) {
      return {};
    }
  }

  getInsuranceOptOut(cartToken) {
    const preference = this.getInsurancePreference();
    const defaultOptOut = this.dataset.insuranceDefaultEnabled !== 'true';
    if (typeof preference.optOut !== 'boolean') return defaultOptOut;
    // A new cart (e.g. after checkout) starts from the theme default again.
    if (cartToken && preference.cartToken && preference.cartToken !== cartToken) {
      try {
        localStorage.removeItem(this.insuranceStorageKey);
      } catch (error) {
        // Storage unavailable; fall back to the default.
      }
      return defaultOptOut;
    }
    if (cartToken && !preference.cartToken) this.setInsuranceOptOut(preference.optOut, cartToken);
    return preference.optOut;
  }

  setInsuranceOptOut(optedOut, cartToken) {
    try {
      localStorage.setItem(this.insuranceStorageKey, JSON.stringify({
        optOut: optedOut,
        cartToken: cartToken || ''
      }));
    } catch (error) {
      console.error('Unable to save shipping insurance preference:', error);
    }
  }

  syncInsuranceUI() {
    const optedOut = this.getInsuranceOptOut(this.currentCartToken || '');
    const toggle = this.querySelector('[data-cart-insurance-toggle]');
    const subtotalWithInsurance = this.querySelector('[data-cart-subtotal-with-insurance]');
    const subtotalWithoutInsurance = this.querySelector('[data-cart-subtotal-without-insurance]');

    if (toggle) toggle.checked = !optedOut;
    if (subtotalWithInsurance) subtotalWithInsurance.hidden = optedOut;
    if (subtotalWithoutInsurance) subtotalWithoutInsurance.hidden = !optedOut;
  }

  clearInsuranceError() {
    const errorElement = this.querySelector('[data-cart-insurance-error]');
    if (!errorElement) return;
    errorElement.textContent = '';
    errorElement.hidden = true;
  }

  showInsuranceError(error) {
    const errorElement = this.querySelector('[data-cart-insurance-error]');
    if (!errorElement) return;
    errorElement.textContent = error && error.message ? error.message : window.cartStrings.error;
    errorElement.hidden = false;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  // Runs after every quantity change / remove (success or failure).
  // Re-check insurance so it is removed when the last real item leaves the cart.
  disableLoading(...args) {
    super.disableLoading(...args);
    const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    if (drawer && typeof drawer.scheduleInsuranceReconcile === 'function') {
      drawer.scheduleInsuranceReconcile(150);
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
